import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { CardHeader, SwipeableDrawer, useMediaQuery, Card, CardContent, Box, Button,
  FormHelperText, Grid, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Divider,
  Tooltip} from "@mui/material";
import { useGetMainList, useRecordContext, useSaveContext } from "ra-core";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { PermissionResponseDto, RoleInput, RoleResponseDto } from "~/gen/api/user";
import { RecPatch } from "~/dataProvider/tools";
import { ORGANIZATION_USERS, SECUREFILE_ONLY } from "~/constants";
import { debounce } from "lodash";


const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(100).required('Display Name is required'),
    value: Yup.string().max(100).required('Value is required').matches(/^\s*\S*\s*$/, 'Value should not contain white spaces'),
    description: Yup.string().max(255),
  })

export interface EditRoleProps {
  open: boolean;
  onClose: () => void;
}

const EditRole: FC<EditRoleProps> = ({
  open,
  onClose
}) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const record = useRecordContext<RoleResponseDto&RecPatch>()
  const mode = (record?.roleId) ? 'edit' : 'create'

  const { data: pData } = useGetMainList<PermissionResponseDto&RecPatch>('org-roles-permits', pages(), sorts(), {})
  const builtInPermists = Object.values(pData ?? {})
    .filter(p => 
         p.name !== ORGANIZATION_USERS // ORGANIZATION_USERS permit is not assignable!!
      && p.name !== SECUREFILE_ONLY    // SECUREFILE_ONLY permit is not assignable!!
    ) 
    .sort((a, b) => (a.name ?? '') < (b.name ?? '') ? -1 : 1)
  
  const initialValues = {
    submit: '',
    permission: '1',
    name: record?.name || '',
    value: record?.value || '',
    description: record?.description || '',
  }
  
  const [states, setStates] = useState({
    prevSelection: new Set<number>(),
    added: new Set<number>(),
    removed: new Set<number>(),
  })
  const gatherPermitIds = () => {
    const ids = [...new Set([
      ...(states.prevSelection.values()), 
      ...(states.added.values()),
    ].filter(x => !states.removed.has(x))).values()]
    return ids
  }

  const validate = () => {
    const error = {} as any
    const permitIds = gatherPermitIds()
    if (permitIds.length === 0) {
      error['permission'] = 'At least one permission is needed'
    }
    return Object.keys(error).length > 0 ? error : undefined
  }

  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        onClose()
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const payload : RoleInput = {...values,
      permissionIds: [...(states.prevSelection.values()), ...(states.added.values())].filter(x => !states.removed.has(x)),
      roleId: record?.roleId,
      ...({permission: undefined}),
    }
    save(payload, options)
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, touched, isSubmitting, setFieldError, setFieldTouched, setValues, resetForm, handleChange: formikOnChange, validateField } = formikCtrl

  useEffect(() => {
    if (!open) {
      resetForm()
      setStates({
        prevSelection: new Set<number>(),
        added: new Set<number>(),
        removed: new Set<number>(),
      })

    } else if (!!record && record.id) {
      setValues({
        name: record.name || '',
        value: record.value || '',
        description: record.description || '',
        permission: '1',
      } as any)
      setStates({
        prevSelection: new Set((record.permissions ?? []).map(p => p.id ?? 0)),
        added: new Set<number>(),
        removed: new Set<number>(),
      })
    }
  }, [record, setValues, resetForm, open])

  const actionRef = useRef((s:string) => {})
  const debounceAction = useMemo(() => debounce((s: string) => actionRef.current(s), 1500), [])

  const handleDisplayNameChange = useMemo(() => {
    const autoTrim = (s:string) => {
      const trimed = s && typeof(s) === 'string' ? s.trim() : s
      formikOnChange({target:{value:trimed, name: 'name'}})
    }
    actionRef.current = autoTrim
    if (mode === 'create' && !touched.value) {
      const handleChangeAndPopulate = (e: ChangeEvent<any>) => {
        const v0 : string = e.target.value
        setValues(vals => ({...vals,
          name: v0,
          value: v0.trim().replace(/\s/g, '')
        }))
        setFieldTouched('name', true)
        validateField('name')
        debounceAction(v0)
      }
      return handleChangeAndPopulate

    } else {        
      const handleChange = (e: ChangeEvent<any>) => {
        formikOnChange(e)
        debounceAction(e.target.value)
      }
      return handleChange
    }
  }, [debounceAction, formikOnChange, mode, setFieldTouched, setValues, touched.value, validateField])

  const cancel = () => {
    resetForm()
    setStates({
      prevSelection: new Set<number>(),
      added: new Set<number>(),
      removed: new Set<number>(),
    })
    onClose()
  }
  return (
    <SwipeableDrawer
      anchor="right"
      onOpen={() => {}}
      onClose={cancel}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <OdForm formikCtrl={formikCtrl}>
        <Card>
          <CardHeader title={mode === 'edit' ? "Edit Role" : "Create Role"} />
          <CardContent>
            <Grid container spacing={4} >
              <Grid item xs={12} >
                <TextField label="Display Name*" name="name" onChange={handleDisplayNameChange} />
              </Grid>
              <Grid item xs={12} >
                <TextField label="Value*" name="value" />
              </Grid>
              <Grid item xs={12} >
                <TextField label="Description" name="description" multiline />
              </Grid>
              <Grid item xs={12}>
                <FormControl sx={{ ml: 3, width: 'calc(100% - 18px)' }} component="fieldset" variant="standard" error={Boolean(touched["permission"] && errors['permission'])}>
                  <FormLabel component="legend">Assign permissions</FormLabel>
                  <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 385px)' }}>
                    <FormGroup>
                      {(builtInPermists ?? []).map(p =>
                        <Tooltip title={p.description ?? ''} key={p.id}>
                          <FormControlLabel
                            control={
                              <Checkbox 
                                checked={!states.removed.has(p.id) && (states.prevSelection.has(p.id) || states.added.has(p.id))} 
                                onChange={(e) => {
                                  const toAdd = e.target.checked
                                  const id = p.id as number
                                  if (toAdd) {
                                    if (states.removed.has(id)) { states.removed.delete(id) } 
                                    else if (!states.prevSelection.has(id)) {
                                      states.added.add(id)
                                    }
                                  } else {
                                    if (states.added.has(id)) { states.added.delete(id) }
                                    else if (states.prevSelection.has(id)) {
                                      states.removed.add(id)
                                    }
                                  }
                                  setFieldError('permission', undefined)
                                  setStates(v => ({...v}))
                                }} 
                                name={p.name || ''} 
                              />
                            }
                            key={p.id || -1}
                            label={p.name || ''}
                          />
                        </Tooltip>
                      )}
                    </FormGroup>
                  </Box>
                  {Boolean(touched["permission"] && errors["permission"]) && 
                    <FormHelperText>{touched["permission"] && errors["permission"]}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              onClick={cancel}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting || saving}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Card>
      </OdForm>
    </SwipeableDrawer>

  )
}

export default EditRole;
